import {apiUrl} from '../utils/config';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/*
 * The dashboard and API are separate origins. The CSRF cookie is host-only
 * for games.api.ayesearch.com, so JavaScript running on the dashboard origin
 * should not rely on document.cookie to read it. The backend CSRF endpoint
 * returns the token in JSON; keep that value in memory and send it as the
 * X-CSRFToken header for unsafe requests.
 */
let csrfToken = '';

export const setCsrfToken = (token: string): void => {
  csrfToken = token.trim();
};

export const clearCsrfToken = (): void => {
  csrfToken = '';
};

const parseResponse = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }

  return response.text().catch(() => '');
};

const getErrorMessage = (
  status: number,
  data: unknown,
): string => {
  if (typeof data === 'object' && data !== null) {
    const record = data as Record<string, unknown>;
    const message = record.detail ?? record.message;

    if (message !== undefined && message !== null) {
      return String(message);
    }
  }

  return `Request failed (${status})`;
};

export async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers || {});
  const method = (options.method || 'GET').toUpperCase();

  if (
    !(options.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  if (
    csrfToken &&
    !['GET', 'HEAD', 'OPTIONS'].includes(method)
  ) {
    headers.set('X-CSRFToken', csrfToken);
  }

  const response = await fetch(apiUrl(path), {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const data = await parseResponse(response);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      getErrorMessage(response.status, data),
      data,
    );
  }

  return data as T;
}
